import React, { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { fmtDateShort } from "@/lib/format";

const eur = (n) => `€ ${Number(n || 0).toLocaleString("it-IT", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

export function waNumber(p) {
  let d = String(p || "").replace(/[^\d+]/g, "");
  if (d.startsWith("+")) return d.slice(1);
  if (d.startsWith("00")) return d.slice(2);
  if (d.startsWith("3") || d.startsWith("0")) return `39${d}`;
  return d;
}

export function fillTemplate(text, r) {
  const map = {
    nome: r.first_name || r.name, mesi: r.months_late, ultima_data: r.last_date ? fmtDateShort(r.last_date) : "—",
    ultimo_importo: r.last_amount ? eur(r.last_amount) : "—", impegno: r.pledge_amount ? eur(r.pledge_amount) : eur(r.avg_amount),
    frequenza: r.frequency || "",
  };
  return (text || "").replace(/\{(\w+)\}/g, (m, k) => (map[k] ?? m));
}

export function ContactDialog({ row, channel, onClose }) {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["late-templates"], queryFn: async () => (await api.get("/late-donors/templates")).data.templates });
  const tpls = (data || []).filter((t) => t.channel === channel);
  const [tid, setTid] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [to, setTo] = useState("");
  const missing = row && !(channel === "whatsapp" ? row.phone : row.email);

  useEffect(() => { if (row) setTo((channel === "whatsapp" ? row.phone : row.email) || ""); }, [row, channel]);

  useEffect(() => {
    if (!row || !tpls.length) return;
    const t = tpls.find((x) => x.id === tid) || tpls[row.has_pledge ? Math.min(1, tpls.length - 1) : 0];
    setTid(t.id); setSubject(fillTemplate(t.subject, row)); setBody(fillTemplate(t.body, row));
  }, [row, channel, data]); // eslint-disable-line react-hooks/exhaustive-deps

  const pick = (id) => { const t = tpls.find((x) => x.id === id); setTid(id); setSubject(fillTemplate(t.subject, row)); setBody(fillTemplate(t.body, row)); };
  const log = () => api.post(`/late-donors/${row.donor_id}/contact`, { channel, template_name: tpls.find((t) => t.id === tid)?.name })
    .then(() => qc.invalidateQueries({ queryKey: ["late-donors"] })).catch(() => {});

  async function send(kind) {
    const dest = to.trim();
    if (channel === "whatsapp" && waNumber(dest).length < 8) return toast.error("Inserisci un numero di telefono valido");
    if (channel === "email" && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(dest)) return toast.error("Inserisci un'email valida");
    if (dest !== ((channel === "whatsapp" ? row.phone : row.email) || "")) {
      try { await api.patch(`/donors/${row.donor_id}`, channel === "whatsapp" ? { whatsapp: dest } : { email: dest }); }
      catch (e) { return toast.error(e.response?.data?.detail?.[0]?.msg || "Impossibile salvare il contatto"); }
    }
    let url;
    if (channel === "whatsapp") url = `https://wa.me/${waNumber(dest)}?text=${encodeURIComponent(body)}`;
    else if (kind === "gmail") url = `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(dest)}&su=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    else url = `mailto:${dest}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.open(url, "_blank", "noopener");
    log(); qc.invalidateQueries({ queryKey: ["late-donors"] }); toast.success("Contatto registrato"); onClose();
  }

  return (
    <Dialog open={!!row} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg bg-white" data-testid="late-contact-dialog">
        <DialogHeader>
          <DialogTitle>{channel === "whatsapp" ? "Messaggio WhatsApp" : "Email"} a {row?.name}</DialogTitle>
          <div className="text-[12px] text-stone-500">{channel === "whatsapp" ? "Numero WhatsApp" : "Email"}{missing ? " — non presente in anagrafica: inseriscilo, verrà salvato sul donatore" : ""}</div>
        </DialogHeader>
        <Input value={to} onChange={(e) => setTo(e.target.value)} placeholder={channel === "whatsapp" ? "Es. 333 1234567 o +41 79 123 4567" : "nome@esempio.it"}
          className={missing ? "border-amber-400" : ""} data-testid="late-to"/>
        <Select value={tid} onValueChange={pick}>
          <SelectTrigger data-testid="late-template-select"><SelectValue placeholder="Template…"/></SelectTrigger>
          <SelectContent>{tpls.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
        </Select>
        {channel === "email" && <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Oggetto" data-testid="late-subject"/>}
        <Textarea rows={9} value={body} onChange={(e) => setBody(e.target.value)} data-testid="late-body"/>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          {channel === "whatsapp"
            ? <Button onClick={() => send()} className="bg-[#25D366] hover:bg-[#1ebe5b] text-white" data-testid="late-open-wa">Apri WhatsApp</Button>
            : <>
                <Button variant="outline" onClick={() => send("mailto")} data-testid="late-open-mailto">App email</Button>
                <Button onClick={() => send("gmail")} className="bg-[#1C352D] hover:bg-[#264A3E]" data-testid="late-open-gmail">Apri in Gmail</Button>
              </>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
